import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

const theme = createTheme({
  palette: {
    primary: {
      main: '#007AFF', // Apple system blue
      dark: '#0051D5',
      light: '#40A9FF',
    },
    secondary: {
      main: '#5856D6', // Apple system indigo
      dark: '#4B4ACF',
      light: '#7B7AE8',
    },
    accent: {
      main: '#34C759', // Apple system green
      dark: '#30B454',
      light: '#5DD579',
    },
    background: {
      default: '#FFFFFF',
      paper: '#F2F2F7', // Apple system gray 6
    },
    text: {
      primary: '#1D1D1F', // Apple text primary
      secondary: '#6D6D70', // Apple text secondary
    },
    grey: {
      50: '#FAFAFA',
      100: '#F2F2F7',
      200: '#E5E5EA',
      300: '#D1D1D6',
      400: '#C7C7CC',
      500: '#AEAEB2',
      600: '#8E8E93',
      700: '#636366',
      800: '#48484A',
      900: '#1D1D1F',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      color: '#1D1D1F',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      color: '#1D1D1F',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
      color: '#1D1D1F',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      color: '#1D1D1F',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      color: '#1D1D1F',
    },
    body1: {
      fontSize: '1.0625rem', // 17px - Apple's preferred body size
      lineHeight: 1.47,
      color: '#1D1D1F',
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.9375rem', // 15px
      lineHeight: 1.43,
      color: '#6D6D70',
      fontWeight: 400,
    },
    caption: {
      fontSize: '0.75rem', // 12px
      lineHeight: 1.33,
      color: '#8E8E93',
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 12, // Apple's preferred border radius
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          padding: '12px 24px',
          fontSize: '1.0625rem',
          fontWeight: 500,
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 122, 255, 0.25)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
          color: 'white',
          '&:hover': {
            background: 'linear-gradient(135deg, #0051D5 0%, #4B4ACF 100%)',
          },
        },
        outlined: {
          borderColor: '#007AFF',
          color: '#007AFF',
          borderWidth: '1.5px',
          '&:hover': {
            borderColor: '#0051D5',
            backgroundColor: 'rgba(0, 122, 255, 0.04)',
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E5E5EA',
          backgroundColor: '#FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          '&:hover': {
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-4px)',
            borderColor: '#D1D1D6',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid #E5E5EA',
          boxShadow: 'none',
          color: '#1D1D1F',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.875rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#F2F2F7',
            border: 'none',
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused fieldset': {
              border: '2px solid #007AFF',
            },
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
);